// Product-specific supplemental content
// Map product handles to their unique content sections

export interface ProductExtras {
  whatYouWillLearn?: string[];
  whatsIncluded?: string[];
  whoThisIsFor?: string;
  resultsYouCanExpect?: string[];
  testimonial?: {
    quote: string;
    authorName: string;
    authorTitle: string;
  };
  faqItems?: Array<{
    question: string;
    answer: string;
  }>;
}

export const productExtras: Record<string, ProductExtras> = {
  // Founding Team (8 Hours) Accelerator Package
  "founder-consulting-package": {
    whatYouWillLearn: [
      "Transform your early-stage idea into an investor-ready business model",
      "Develop a compelling pitch deck that resonates with VCs and angel investors",
      "Master client engagement strategies that drive revenue and retention",
      "Build financial projections and metrics that demonstrate business viability",
      "Create a strategic roadmap from vision to market validation"
    ],
    whatsIncluded: [
      "8 hours of direct 1:1 strategic advisory sessions",
      "5 additional hours of deep research and analysis on your business",
      "25 × 20-minute advisory sessions (25 Advisory Tokens valued at USD $40 each)",
      "Pitch deck development, refinement, and investor-ready presentation",
      "Client engagement strategy framework and implementation guide",
      "Ongoing support and guidance throughout the accelerator period"
    ],
    whoThisIsFor: "Ambitious founders who have a validated idea and are ready to build serious momentum. You're either preparing for fundraising, launching your MVP, or scaling from early traction—and you need strategic clarity, investor-ready materials, and a trusted advisor to help you navigate critical decisions without wasting time or resources.",
    resultsYouCanExpect: [
      "A polished, investor-ready pitch deck that tells your story with confidence and clarity",
      "A validated business model with clear revenue pathways and growth potential",
      "Strategic roadmap that prioritizes high-impact actions over busy work",
      "Confidence to engage with investors, partners, and clients at a professional level",
      "Ongoing advisory support through 25 reusable tokens for future strategic guidance"
    ],
    testimonial: {
      quote: "Peter's guidance transformed our pitch from a vague idea into a funded reality. His strategic insights and hands-on approach helped us secure USD $500K in seed funding within 3 months.",
      authorName: "Anonymous",
      authorTitle: "Anonymous"
    },
    faqItems: [
      {
        question: "How long does the accelerator program last?",
        answer: "The Founding Team Accelerator is structured as an intensive 8-hour engagement with an additional 5 hours of deep research. Sessions are typically spread over 4-6 weeks to allow time for implementation between advisory calls."
      },
      {
        question: "What stage should my startup be at to benefit from this?",
        answer: "This program is ideal for early-stage founders who have validated their idea and are ready to build investor confidence. Whether you're pre-revenue or have initial traction, the program adapts to your specific stage and needs."
      },
      {
        question: "Can I use my Advisory Tokens after the main program ends?",
        answer: "Yes! Your 25 Advisory Tokens remain active and can be used for follow-up sessions, pitch practice, investor prep, or strategic guidance as your business evolves."
      },
      {
        question: "What if I need help beyond the included hours?",
        answer: "Additional Advisory Tokens can be purchased separately to continue working together. Many founders extend their engagement as they progress through fundraising or scale their operations."
      }
    ]
  },

  // Founder Diagnostic
  "founder-diagnostic": {
    whatYouWillLearn: [
      "Gain data-backed clarity on your market position and competitive landscape",
      "Understand exactly where your business stands and what gaps need addressing",
      "Discover the most strategic path forward for your specific business model",
      "Learn which validation methods and metrics matter most at your stage",
      "Identify the smartest next moves for go-to-market or investment readiness"
    ],
    whatsIncluded: [
      "2 hours of targeted market and competitor research",
      "3 hours of strategy development tailored to your business",
      "1-hour delivery session via Zoom with detailed findings presentation",
      "Comprehensive 5-7 page Founder Diagnostic Report",
      "Priority roadmap of actionable next steps",
      "Evidence-based recommendations for growth, funding, or product-market fit validation"
    ],
    whoThisIsFor: "Founders who feel stuck or uncertain about their next move. You have a product or idea but need objective, data-driven insights on market positioning, competitive dynamics, or strategic direction. Perfect for those who want clarity before committing to a full acceleration program or major business pivot.",
    resultsYouCanExpect: [
      "A comprehensive diagnostic report with actionable insights and strategic recommendations",
      "Clear understanding of your competitive advantages and market opportunities",
      "Validated roadmap of priority actions ranked by impact and feasibility",
      "Confidence in your strategic direction backed by research and market analysis",
      "Foundation for informed decisions on product development, go-to-market, or fundraising"
    ],
    testimonial: {
      quote: "The Founder Diagnostic gave us the clarity we desperately needed. Peter's research uncovered market opportunities we'd completely missed, and his strategic roadmap became our execution blueprint for the next 6 months.",
      authorName: "Anonymous",
      authorTitle: "Anonymous"
    },
    faqItems: [
      {
        question: "How is this different from a regular consulting call?",
        answer: "The Founder Diagnostic is a structured, week-long process that includes dedicated research hours. Unlike a single consultation, this provides you with written insights, competitive analysis, and a strategic roadmap document you can reference and share with your team or advisors."
      },
      {
        question: "Do I need to have completed the Discovery Call first?",
        answer: "While recommended, it's not mandatory. The Discovery Call helps us understand your goals upfront, making the Diagnostic even more targeted. If you're ready to dive deep immediately, we can start with the Diagnostic."
      },
      {
        question: "What happens after I receive the diagnostic report?",
        answer: "You'll have clear next steps and can choose to implement independently or move into an Acceleration Blueprint engagement for hands-on implementation support."
      }
    ]
  },

  // Founder Discovery Call (Free)
  "founder-discovery-call-free-30-minutes": {
    whatYouWillLearn: [
      "Clarify where you are in your founder journey and what stage you've reached",
      "Identify your biggest immediate challenges and potential blind spots",
      "Understand whether the Founder Accelerator program aligns with your goals",
      "Discover the most productive next steps based on your current traction",
      "Learn what investors and advisors look for at your specific business stage"
    ],
    whatsIncluded: [
      "30-minute focused strategy session via Zoom or in-person (Auckland only)",
      "Pre-call questionnaire to maximize our time together",
      "Strategic assessment of your business stage and traction",
      "Clear guidance on next steps and accelerator program fit",
      "Honest feedback on opportunities, gaps, and realistic pathways forward"
    ],
    whoThisIsFor: "Early-stage founders exploring whether advisory support makes sense at this point in their journey. You're evaluating options, want honest feedback, and prefer to understand what working together looks like before committing to a paid engagement. No pressure, just clarity.",
    resultsYouCanExpect: [
      "Clear understanding of where you stand in your founder journey",
      "Honest assessment of your biggest challenges and opportunities",
      "Specific guidance on the most productive next steps for your stage",
      "Clarity on whether the Founder Accelerator is the right fit for your goals",
      "A 30-minute conversation that saves you weeks of trial and error"
    ],
    testimonial: {
      quote: "I wasn't sure if I was ready for advisory support, but Peter's Discovery Call showed me exactly where I stood and what I needed to focus on next. That 30 minutes saved me months of wandering in the wrong direction.",
      authorName: "Anonymous",
      authorTitle: "Anonymous"
    },
    faqItems: [
      {
        question: "Is this really free with no strings attached?",
        answer: "Yes, completely free. The purpose is mutual discovery — to see if we're the right fit to work together and whether the accelerator program can genuinely help you achieve your goals."
      },
      {
        question: "What should I prepare before the call?",
        answer: "After booking, you'll receive a short questionnaire covering your business idea, stage, traction, and goals. Completing this helps us use the 30 minutes effectively and dive straight into strategic discussion."
      },
      {
        question: "Will I be pressured to buy something?",
        answer: "No. This is about clarity and fit. If the accelerator isn't right for you, I'll tell you honestly and point you toward resources that might help instead."
      }
    ]
  },

  // Go-To-Market Strategy Course
  "go-to-market-strategy-how-startups-launch-learn-and-scale": {
    whatYouWillLearn: [
      "Build a Go-To-Market framework that aligns product, customer, and channels",
      "Define your TAM, SAM, and SOM with investor-grade accuracy",
      "Craft your 'Plan on a Page' — a visual GTM strategy that drives execution",
      "Design marketing activity timelines (daily, weekly, quarterly) that compound results",
      "Build sales funnels that convert awareness into recurring revenue",
      "Align your GTM actions with investor expectations and compelling storytelling"
    ],
    whatsIncluded: [
      "40-minute on-demand video lesson with real startup case studies",
      "GTM Framework Template (editable PDF + Google Slides)",
      "Plan-on-a-Page Worksheet for visual strategy mapping",
      "Sales Funnel Builder Template (Google Sheet + Excel)",
      "TAM/SAM/SOM Market Sizing Guide with examples",
      "GTM Activation Checklist for immediate implementation"
    ],
    whoThisIsFor: "Founders preparing to launch or scale who need a structured approach to reaching customers. You have a product or MVP but feel scattered in your marketing efforts. You want a clear, visual GTM plan that your team can execute and investors can understand.",
    resultsYouCanExpect: [
      "A complete Go-To-Market framework customized to your business model",
      "Visual 'Plan on a Page' strategy that drives team alignment and execution",
      "Investor-ready TAM/SAM/SOM market sizing with supporting data",
      "Sales funnel structure that turns leads into recurring revenue",
      "Marketing timeline that prioritizes high-impact actions and avoids busy work"
    ],
    testimonial: {
      quote: "This course gave us the GTM structure we'd been missing. We went from scattered marketing efforts to a cohesive strategy that investors understood and our team could execute. Revenue grew 3x in the following quarter.",
      authorName: "Anonymous",
      authorTitle: "Anonymous"
    },
    faqItems: [
      {
        question: "Is this suitable for B2B or B2C businesses?",
        answer: "Both! The frameworks adapt to any business model. The course includes examples from both B2B SaaS and B2C marketplaces, showing how to customize the approach for your audience."
      },
      {
        question: "How technical is this course?",
        answer: "Not technical at all — it's strategic and visual. You'll learn frameworks and thinking models, not code or complex tools. If you can use Google Slides and Excel, you're all set."
      },
      {
        question: "Can I implement this alongside my current marketing?",
        answer: "Absolutely. The GTM framework helps you organize what you're already doing and identify what's missing. Many founders use it to audit their current efforts and refine their approach."
      }
    ]
  },

  // Pitching to Investors Course
  "pitching-to-investors-how-to-tell-your-story-and-win-the-room": {
    whatYouWillLearn: [
      "Define your company purpose in one compelling sentence that investors remember",
      "Describe customer pain and your solution with clarity and credibility",
      "Frame your market opportunity with data that builds confidence",
      "Present traction and team strength without exaggeration or underselling",
      "End with a clear, strategic 'ask' that opens the door to next steps",
      "Avoid the common pitch mistakes that kill deals before they start"
    ],
    whatsIncluded: [
      "20-minute on-demand video lesson from accelerator orientation sessions",
      "Editable Pitch Deck Template (Google Slides + PowerPoint)",
      "Investor Q&A Prep Sheet covering the top 15 questions VCs ask",
      "'Why Now' Trend Mapping Guide to strengthen your timing narrative",
      "Pitch Reflection Worksheet to refine your story before presenting"
    ],
    whoThisIsFor: "Founders preparing for fundraising who need to articulate their vision with confidence and clarity. You have traction or a validated idea but struggle to tell your story in a way that resonates with investors. You want a proven framework to structure your pitch and avoid common mistakes.",
    resultsYouCanExpect: [
      "A compelling pitch narrative that investors remember and respond to",
      "Professional pitch deck ready to present to angels, VCs, or strategic partners",
      "Confidence to answer tough investor questions without stumbling",
      "Clear understanding of what investors look for at different funding stages",
      "Framework to refine your story and improve with each pitch iteration"
    ],
    testimonial: {
      quote: "I thought I had a good pitch until I took this course. Peter's framework helped me restructure everything — and I closed my first angel investor within two weeks of applying what I learned.",
      authorName: "Anonymous",
      authorTitle: "Anonymous"
    },
    faqItems: [
      {
        question: "Do I need to have fundraising experience to benefit from this?",
        answer: "Not at all. This course is designed for first-time founders who are learning to pitch. If you're preparing for your first investor meeting, this is the perfect starting point."
      },
      {
        question: "What's included in the pitch deck template?",
        answer: "A fully editable template with recommended slide structure, placeholder content, and design guidelines. It follows the format that works across seed and Series A pitches."
      },
      {
        question: "Can I get feedback on my actual pitch?",
        answer: "Yes! After completing the course, you can book a 1-on-1 Pitch Review Session (requires 1 Advisory Token) to get personalized feedback and refinement."
      }
    ]
  },

  // PR Superpowers Course
  "pr-superpowers-building-credibility-in-a-noisy-world": {
    whatYouWillLearn: [
      "Craft a newsworthy story angle that journalists and audiences want to share",
      "Build a professional press kit with media-ready assets that look polished",
      "Develop authentic relationships with journalists and industry voices",
      "Write the perfect media pitch — short, personal, and punchy",
      "Leverage social channels as your modern PR amplifier for earned media",
      "Turn one-time coverage into ongoing brand credibility and trust"
    ],
    whatsIncluded: [
      "20-minute on-demand video lesson with real media outreach examples",
      "PR Toolkit Template (Google Docs + PDF) for immediate use",
      "Perfect Pitch Email Template that gets responses",
      "Media Outreach Tracker (Google Sheet) to manage relationships",
      "PR Reflection Worksheet to define your unique story angle"
    ],
    whoThisIsFor: "Founders who want media visibility but have no PR experience or budget for agencies. You understand that credibility matters but don't know how to get journalists interested in your story. You're ready to build your own PR engine from scratch.",
    resultsYouCanExpect: [
      "A newsworthy story angle that journalists and audiences want to cover",
      "Professional press kit ready to send to media contacts immediately",
      "Strategic media outreach plan with personalized pitches that get responses",
      "Earned media coverage that builds credibility and amplifies your brand",
      "Long-term PR skills that compound your visibility without ongoing agency costs"
    ],
    testimonial: {
      quote: "Before this course, we had zero media presence. Within a month of applying Peter's PR framework, we were featured in three major tech publications. The credibility boost accelerated our sales pipeline significantly.",
      authorName: "Anonymous",
      authorTitle: "Anonymous"
    },
    faqItems: [
      {
        question: "Do I need existing media connections to make this work?",
        answer: "No! The course teaches you how to build those connections from scratch. You'll learn how to research the right journalists, personalize your outreach, and create relationships that lead to coverage."
      },
      {
        question: "How long does it typically take to get media coverage?",
        answer: "It varies, but most founders who consistently apply the framework see their first media mention within 30-60 days. The key is regular, strategic outreach — not one-off blasts."
      },
      {
        question: "Is this different from paid advertising?",
        answer: "Completely different. This is about earned media — getting journalists to cover your story because it's newsworthy. The credibility from earned media far exceeds paid ads and costs nothing but your time and strategy."
      }
    ]
  },

  // Scaling Up: Fintech Course
  "scaling-up-strategies-for-fintech-companies": {
    whatYouWillLearn: [
      "Identify the defining traits and challenges of the fintech scale-up stage",
      "Build and manage a predictable B2B sales pipeline using SaaS metrics",
      "Implement tools and technologies that support high-growth fintech teams",
      "Adopt proven lead generation and conversion strategies for B2B fintech",
      "Apply best practices from real case studies of Asian fintech scale-ups",
      "Navigate regulatory considerations while maintaining growth velocity"
    ],
    whatsIncluded: [
      "40-minute on-demand video lesson with fintech-specific insights",
      "Fintech Pipeline Management Template (Google Sheet + Excel)",
      "SaaS Metrics Dashboard Guide (PDF) for tracking growth indicators",
      "Scale-Up Strategy Worksheet tailored for fintech companies",
      "Case Study Summary Pack featuring five Asian fintech scale-up examples"
    ],
    whoThisIsFor: "Fintech founders who have product-market fit and are ready to scale operations beyond the early-stage hustle. You're transitioning from founder-led sales to building repeatable growth systems, and you need proven frameworks to manage the complexity of scaling in regulated markets.",
    resultsYouCanExpect: [
      "Predictable B2B sales pipeline with clear visibility into conversion metrics",
      "Growth dashboard tracking the SaaS metrics that matter for fintech scale-ups",
      "Operational framework to scale your team and technology without chaos",
      "Strategic clarity on lead generation and customer acquisition for regulated markets",
      "Confidence backed by real fintech case studies and battle-tested strategies"
    ],
    testimonial: {
      quote: "As a fintech founder, this course was exactly what we needed. Peter's real-world examples and pipeline framework helped us systematize our growth and hit USD $2M ARR within 18 months.",
      authorName: "Anonymous",
      authorTitle: "Anonymous"
    },
    faqItems: [
      {
        question: "Is this only relevant for B2B fintech companies?",
        answer: "While the examples lean B2B, the principles apply to any fintech business transitioning from early traction to systematic growth. B2C fintechs will find valuable insights on metrics, pipeline thinking, and scaling operations."
      },
      {
        question: "Do I need technical knowledge to understand this?",
        answer: "No. The course focuses on growth strategy, metrics, and operational systems — not technical infrastructure. It's designed for founders and growth leaders, not engineers."
      },
      {
        question: "What if my fintech is outside of Asia?",
        answer: "The strategies are universal. While case studies feature Asian companies, the frameworks for pipeline management, metrics, and growth apply to fintech businesses globally."
      }
    ]
  },

  // Successful Meetings Course
  "founder-meeting-mastery-professional-habits-for-serious-builders": {
    whatYouWillLearn: [
      "Set up meetings professionally with clear logistics and defined purpose",
      "Master calendar invites, links, and location details that signal competence",
      "Apply professional email standards for investor and partner communication",
      "Define meeting objectives and keep conversations on track efficiently",
      "Run confident, structured meetings using a repeatable framework",
      "Implement follow-up discipline that signals reliability and execution strength"
    ],
    whatsIncluded: [
      "20-minute on-demand video with practical execution examples",
      "Founder Meeting Agenda Template for consistent structure",
      "Investor Outreach Email Template that opens doors",
      "Calendar Invite Structure Guide for professional coordination",
      "Post-Meeting Follow-Up Script to maintain momentum",
      "Meeting Planning Checklist for flawless execution"
    ],
    whoThisIsFor: "First-time founders or early-stage builders who want to project professionalism in every interaction. You're meeting investors, advisors, or strategic partners and need to make sure the details signal competence and reliability. You understand that small execution details create lasting impressions.",
    resultsYouCanExpect: [
      "Professional meeting coordination that signals competence and builds trust",
      "Consistent follow-up discipline that keeps momentum alive after meetings",
      "Investor and partner confidence in your execution capabilities",
      "Time saved by using proven templates instead of reinventing every time",
      "Systematic habits that free your attention for strategic thinking"
    ],
    testimonial: {
      quote: "I didn't realize how unprofessional my meeting coordination looked until I took this course. The templates and frameworks immediately elevated how partners and investors perceived me. Small details create big impressions.",
      authorName: "Anonymous",
      authorTitle: "Anonymous"
    },
    faqItems: [
      {
        question: "Isn't this just basic stuff everyone already knows?",
        answer: "You'd think so, but most first-time founders don't execute these basics consistently. Investors notice. This course gives you a systematic approach that becomes automatic, freeing your attention for strategic thinking."
      },
      {
        question: "Who should take this course?",
        answer: "Early-stage founders, student entrepreneurs, and anyone transitioning from informal to professional environments. If you're about to meet investors, advisors, or partners, this sets the right foundation."
      },
      {
        question: "Will this actually help me get funded?",
        answer: "Directly? No. But investors invest in founders they trust. Professional habits signal that you execute with discipline — which compounds into trust over time. Sloppy meeting coordination creates doubt before you even pitch."
      }
    ]
  },

  // Understanding Venture Capital Course
  "mastering-venture-capital-judgment-founder-edition": {
    whatYouWillLearn: [
      "Understand the VC mental model: how funds evaluate risk, optionality, and timing",
      "Learn judgment criteria top funds use beyond pitch decks and projections",
      "Decode the founder signals that investors treat as green lights or red flags",
      "Master the VC playbook for truth-seeking and filtering noise from signal",
      "Build a sharper internal decision framework grounded in clarity, not hype",
      "Accelerate your learning and iteration pace using investor-grade thinking"
    ],
    whatsIncluded: [
      "20-minute deep-dive video session distilling VC thinking patterns",
      "Downloadable Founder Worksheet for self-assessment",
      "Case Study Notes analyzing real investment decisions",
      "Investor Signal Decoder guide to reading meeting dynamics",
      "Decision-Making Framework Template for strategic clarity"
    ],
    whoThisIsFor: "Founders who want to think like investors do—not to game the system, but to build better judgment and make smarter decisions under uncertainty. You're serious about understanding how top VCs evaluate risk, opportunity, and founder potential so you can sharpen your own strategic thinking.",
    resultsYouCanExpect: [
      "Investor-grade judgment framework to evaluate opportunities and risks",
      "Ability to decode investor signals and adapt your approach accordingly",
      "Sharper decision-making grounded in clarity rather than hype or fear",
      "Confidence to navigate fundraising conversations with strategic awareness",
      "Accelerated learning curve by thinking through problems the way top VCs do"
    ],
    testimonial: {
      quote: "This course completely changed how I think about my business. Understanding the VC lens helped me refine my strategy, sharpen my narrative, and ultimately close a USD $1.2M seed round with confidence.",
      authorName: "Anonymous",
      authorTitle: "Anonymous"
    },
    faqItems: [
      {
        question: "Do I need to be fundraising to benefit from this?",
        answer: "No. Understanding how investors think makes you a better founder regardless of fundraising timing. The frameworks help you make smarter strategic decisions, build more defensible businesses, and communicate with clarity."
      },
      {
        question: "Is this only relevant for VC-backed companies?",
        answer: "While focused on VC thinking, the judgment frameworks apply broadly. Even bootstrapped founders benefit from understanding how elite investors evaluate market opportunity, team dynamics, and business resilience."
      },
      {
        question: "What makes this different from other fundraising courses?",
        answer: "Most courses teach you how to pitch. This teaches you how to think like an investor — which fundamentally changes how you build, communicate, and position your entire company."
      }
    ]
  },

  // Valuation & Funding 101 Course
  "valuation-funding-101-how-investors-value-startups": {
    whatYouWillLearn: [
      "Understand valuation drivers investors use at pre-revenue and early stages",
      "Interpret the logic behind each funding round from Seed to Series C",
      "Identify how your progress changes perceived value and funding readiness",
      "Compare funding options: angels, VCs, debt, grants, and creative financing",
      "Communicate your valuation story confidently when pitching investors",
      "Avoid common valuation mistakes that kill deal momentum"
    ],
    whatsIncluded: [
      "20-minute on-demand video lesson with visual frameworks",
      "Valuation & Funding Slides Pack (3 framework visuals + explanations)",
      "Investor Logic Worksheet: 'What do they see when they look at me?'",
      "Funding Options Matrix (PDF + Google Sheet) comparing capital sources",
      "Valuation Readiness Checklist for assessing your position"
    ],
    whoThisIsFor: "Founders preparing for fundraising who need to understand how investors value companies and what drives funding decisions. You want to position your ask intelligently, negotiate with confidence, and avoid valuation mistakes that damage credibility or dilute your ownership unnecessarily.",
    resultsYouCanExpect: [
      "Clear understanding of how investors value startups at different stages",
      "Confidence to justify your valuation ask with strategic reasoning",
      "Knowledge of funding alternatives beyond traditional venture capital",
      "Ability to communicate your company's value story in investor language",
      "Framework to assess funding readiness and position yourself strategically"
    ],
    testimonial: {
      quote: "I was confused about valuation until this course. Peter's frameworks made everything click — I understood exactly how investors saw my business and what I needed to demonstrate to justify our ask. We raised at a fair valuation we were proud of.",
      authorName: "Anonymous",
      authorTitle: "Anonymous"
    },
    faqItems: [
      {
        question: "Will this tell me exactly what my company is worth?",
        answer: "No tool gives you a precise number without context. This course teaches you the frameworks investors use, so you can position your company intelligently and justify your valuation in discussions."
      },
      {
        question: "Is this only for tech startups?",
        answer: "The frameworks apply to any high-growth startup seeking venture capital. While examples may lean tech, the valuation logic works across industries from fintech to consumer goods."
      },
      {
        question: "When should I take this course?",
        answer: "Ideally before you start fundraising conversations. Understanding valuation helps you position your company appropriately and avoid premature or unrealistic asks that damage credibility."
      }
    ]
  },

  // Web3 & Future of Money Course
  "web3-the-future-of-money-understanding-the-next-internet": {
    whatYouWillLearn: [
      "Distinguish between money, currency, and value in the digital age",
      "Understand core Web3 technologies: blockchain, protocols, wallets, and tokens",
      "Explore how regulation and decentralization coexist in modern finance",
      "Recognize the role of NFTs, CBDCs, and digital assets in the new economy",
      "Identify how Web3 principles unlock startup opportunities and business models",
      "Navigate the hype and understand the real infrastructure behind innovation"
    ],
    whatsIncluded: [
      "20-minute on-demand video lesson adapted from the Future of Money seminar",
      "Web3 Glossary & Cheat Sheet (PDF) demystifying technical terms",
      "Token & Protocol Map providing visual overview of the ecosystem",
      "Founder Reflection Worksheet: 'Where does my idea fit in Web3?'"
    ],
    whoThisIsFor: "Founders and business leaders curious about Web3 but overwhelmed by hype and technical jargon. You want to understand the real infrastructure, evaluate opportunities intelligently, and determine whether blockchain, tokens, or decentralized models fit your business without getting lost in speculation.",
    resultsYouCanExpect: [
      "Clear understanding of Web3 infrastructure without technical overwhelm",
      "Ability to evaluate Web3 opportunities and separate hype from real innovation",
      "Strategic clarity on whether Web3 fits your business model or customer needs",
      "Confidence to discuss blockchain, NFTs, and digital assets intelligently",
      "Foundation to make informed decisions about exploring Web3 business models"
    ],
    testimonial: {
      quote: "As a non-technical founder exploring blockchain opportunities, this course gave me the foundational understanding I needed. Peter cut through the hype and explained the real infrastructure clearly. Now I can evaluate Web3 opportunities intelligently.",
      authorName: "Anonymous",
      authorTitle: "Anonymous"
    },
    faqItems: [
      {
        question: "Do I need technical knowledge to understand this?",
        answer: "No. The course is designed for founders and business professionals, not developers. Technical concepts are explained in plain language with practical business context."
      },
      {
        question: "Is Web3 just hype or is it real?",
        answer: "Both exist. This course helps you separate genuine infrastructure innovation from speculative hype, so you can make informed decisions about whether Web3 fits your business model."
      },
      {
        question: "Will this help me launch a Web3 startup?",
        answer: "It provides foundational understanding. If you're considering a blockchain or token-based business, this gives you the mental models to evaluate feasibility and positioning. Implementation requires deeper technical partnerships."
      }
    ]
  },

  // Deep Insight Tokens
  "deep-insight-tokens": {
    whatYouWillLearn: [
      "Navigate your product landscape with expert strategic guidance",
      "Understand competitive dynamics and market positioning opportunities",
      "Clarify your business model with actionable operational insights",
      "Receive tailored consulting focused on your specific challenges",
      "Make informed decisions backed by Peter's 20+ years of experience"
    ],
    whatsIncluded: [
      "Flexible consulting time purchased in token units",
      "Each token represents billable consulting hours at USD $40",
      "Can be applied to strategic advisory, market research, or business model analysis",
      "Included as part of Founding Team Accelerator or purchased independently",
      "Use tokens as needed for focused engagements and ongoing support"
    ],
    whoThisIsFor: "Founders who value flexible, on-demand strategic consulting without committing to long-term contracts. You have specific challenges or questions that arise as your business evolves, and you want expert guidance available when you need it—not on a fixed schedule.",
    resultsYouCanExpect: [
      "Flexible access to strategic consulting exactly when challenges arise",
      "Targeted expert guidance on specific decisions without long-term commitments",
      "Cost-effective advisory support that scales with your business needs",
      "Confidence in critical decisions backed by 20+ years of experience",
      "Ongoing strategic partnership that adapts to your evolving priorities"
    ],
    testimonial: {
      quote: "The Deep Insight Tokens gave us flexibility to get expert guidance exactly when we needed it. Peter's targeted advice on our pricing strategy and competitive positioning saved us months of trial and error.",
      authorName: "Anonymous",
      authorTitle: "Anonymous"
    },
    faqItems: [
      {
        question: "How long do tokens last after purchase?",
        answer: "Tokens do not expire. You can use them as needed over time, whether that's weekly sessions or occasional strategic check-ins as your business evolves."
      },
      {
        question: "Can I use tokens for different topics?",
        answer: "Absolutely. Tokens provide flexible consulting time that can be applied to strategy, market analysis, pitch refinement, product direction, or any business challenge you're facing."
      },
      {
        question: "What's the difference between tokens and hourly consulting?",
        answer: "Tokens pre-package consulting time in manageable units, making it easier to budget and plan strategic sessions. The value and expertise delivered are the same as traditional hourly consulting."
      }
    ]
  },

  // Advisor Units (placeholder - update with specific handle when confirmed)
  "advisor-units": {
    whatYouWillLearn: [
      "Access ongoing strategic advisory tailored to your business stage",
      "Receive expert guidance on critical decisions as they arise",
      "Benefit from flexible consulting time allocated as needed",
      "Navigate complex business challenges with experienced support",
      "Build confidence in your strategic direction with expert validation"
    ],
    whatsIncluded: [
      "Dedicated advisory time purchased in flexible units",
      "Strategic consulting focused on your immediate business needs",
      "Access to Peter's network and 20+ years of founder experience",
      "Ongoing support as your business grows and evolves"
    ],
    whoThisIsFor: "Founders and leaders who need consistent strategic support without the structure of a full accelerator program. You value having expert advisory available on-demand as decisions arise, challenges surface, or opportunities emerge—maintaining continuity and strategic clarity as your business evolves.",
    resultsYouCanExpect: [
      "Consistent access to strategic advisory when you need it most",
      "Expert validation on critical decisions before committing resources",
      "Peace of mind knowing you have experienced support available on-demand",
      "Faster, smarter decision-making backed by proven frameworks and experience",
      "Long-term strategic partnership that scales with your business growth"
    ],
    testimonial: {
      quote: "Having Advisor Units available gave us peace of mind. Whenever we hit a strategic crossroads, we could tap into Peter's expertise immediately rather than struggling alone or making costly mistakes.",
      authorName: "Anonymous",
      authorTitle: "Anonymous"
    },
    faqItems: [
      {
        question: "How do Advisor Units differ from other consulting options?",
        answer: "Advisor Units provide flexible, on-demand access to strategic guidance. They're ideal for founders who need ongoing support but don't require a full accelerator program."
      },
      {
        question: "Can I combine Advisor Units with other programs?",
        answer: "Yes. Many founders purchase Advisor Units to extend their engagement beyond a structured program or to get targeted support between major initiatives."
      },
      {
        question: "How quickly can I schedule time with my Advisor Units?",
        answer: "Typically within a few business days. The flexibility of units means you can schedule sessions as urgent needs arise or plan regular check-ins."
      }
    ]
  },

  // Advisory Core - Monthly Retainer
  "advisory-core": {
    whatYouWillLearn: [
      "Clarify your strategy and priorities with experienced outside perspective",
      "Pressure-test assumptions before time or capital is wasted",
      "Prepare effectively for fundraising conversations",
      "Improve your narrative and positioning for investors and partners",
      "Navigate pilots, partnerships, and early customer relationships",
      "Evaluate NZ vs offshore scaling trade-offs with clarity",
      "Manage founder decision fatigue and maintain focus"
    ],
    whatsIncluded: [
      "One 60-minute advisory call per month — focused, founder-led conversations on your most important decisions",
      "Light async support between sessions — short questions, quick sense-checks, and decision framing via email or messaging",
      "Monthly rhythm check-ins — a steady cadence to avoid drift, distraction, and reactive decision-making",
      "Ongoing context — unlike one-off consulting, advice compounds as I learn your business over time"
    ],
    whoThisIsFor: "Early-stage or solo founders who want a trusted advisor, not a cheerleader. You're pre-raise or between raises, building your first or second venture, and you value thinking time and honest challenge. This works well for founders based in New Zealand, Southeast Asia, India, Hong Kong, Singapore, and across the broader Asia-Pacific region.",
    resultsYouCanExpect: [
      "Clearer strategic thinking and prioritisation",
      "Fewer reactive decisions",
      "Stronger investor and partner conversations",
      "Better use of limited time and capital",
      "Increased confidence in difficult trade-offs",
      "A steadier operating rhythm"
    ],
    testimonial: {
      quote: "Having a consistent thinking partner transformed how I approach decisions. Instead of second-guessing myself constantly, I have clarity on what matters and the confidence to act on it.",
      authorName: "Anonymous",
      authorTitle: "Anonymous"
    },
    faqItems: [
      {
        question: "How long should I expect to stay on Advisory Core?",
        answer: "Most founders engage for several months. The value compounds as context builds, but there's no minimum commitment."
      },
      {
        question: "Can I upgrade later?",
        answer: "Yes. Some founders move to deeper engagement once momentum increases or fundraising begins."
      },
      {
        question: "Is this suitable if I'm outside New Zealand?",
        answer: "Yes. I regularly work with founders across Asia-Pacific and internationally."
      },
      {
        question: "What if I need more support in a given month?",
        answer: "We can discuss upgrading or adding ad-hoc sessions if appropriate."
      },
      {
        question: "What is this NOT?",
        answer: "Advisory Core is not a done-for-you consulting package, a pitch deck production service, an accelerator or cohort program, or a substitute for execution. The value is in clarity, judgment, and pattern recognition — not deliverables."
      }
    ]
  },

  // Embedded Advisor (USD $1,000/month)
  "embedded-advisor": {
    whatYouWillLearn: [
      "Navigate fundraising strategy and investor narrative with experienced guidance",
      "Structure and convert commercial pilots into real revenue",
      "Shape board and investor communications with clarity and confidence",
      "Handle strategic prioritisation under constraint and pressure",
      "Manage founder decision-making during high-stakes periods"
    ],
    whatsIncluded: [
      "Two 60-minute advisory calls per month — deep, focused sessions on capital strategy, commercial decisions, and execution trade-offs",
      "Priority async access — faster turnaround on questions, sense-checks, and decision framing between sessions",
      "Commercial and pilot support — assistance with structuring pilots, converting partnerships, and pressure-testing commercial assumptions",
      "Board-level framing assistance — help shaping narratives, papers, and conversations for boards and investors",
      "Ongoing context and continuity — advice improves as understanding of your business deepens"
    ],
    whoThisIsFor: "Founders post-MVP with real traction or pilots underway. Founders actively preparing to raise or currently raising. Teams engaging with boards, angels, or institutional investors. Founders expanding beyond their home market. Operators who value rigorous thinking and honest challenge. This engagement works well for founders based in New Zealand, Southeast Asia, India, Hong Kong, Singapore, and across the broader Asia-Pacific region.",
    resultsYouCanExpect: [
      "Clearer, more confident investor and board conversations",
      "Improved pilot conversion and commercial clarity",
      "Fewer costly strategic missteps",
      "Stronger narrative alignment across stakeholders",
      "Better handling of pressure during critical phases",
      "Increased confidence in high-impact decisions"
    ],
    testimonial: {
      quote: "Having an experienced advisor embedded in our decision-making during fundraising was invaluable. The judgment and pattern recognition saved us from several costly missteps and helped us close our round with confidence.",
      authorName: "Anonymous",
      authorTitle: "Anonymous"
    },
    faqItems: [
      {
        question: "How long do founders typically stay engaged?",
        answer: "Most founders work with me for several months during periods of heightened complexity or transition."
      },
      {
        question: "Can I start on Advisory Core and upgrade later?",
        answer: "Yes. Many founders begin with Advisory Core and move to Embedded Advisor as stakes increase."
      },
      {
        question: "Is this suitable if I already have a board or investors?",
        answer: "Yes. This engagement is particularly useful once governance and investor expectations are active."
      },
      {
        question: "What if I need support beyond the included time?",
        answer: "Additional sessions can be discussed if appropriate and availability allows."
      },
      {
        question: "What is this NOT?",
        answer: "Embedded Advisor is not an execution or fractional executive role, a substitute for a full-time CFO, COO, or GM, a guarantee of fundraising outcomes, or a generic consulting engagement. The value lies in judgment, perspective, and pattern recognition — applied consistently over time."
      }
    ]
  }
};

// Helper function to get product extras by handle
export const getProductExtras = (handle: string): ProductExtras | null => {
  return productExtras[handle] || null;
};
